import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Send, Loader2, Download, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Box } from "./JobActions";
import { get, post, errMsg, hzFile } from "@/lib/api";
import { eur, fdate, fdatetime } from "@/lib/hz";

export const Chat = ({ jobId, meId, otherName }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["messages", jobId], queryFn: () => get(`/jobs/${jobId}/messages`), refetchInterval: 4000, retry: false });
  useEffect(() => { ref.current?.scrollTo({ top: ref.current.scrollHeight, behavior: "smooth" }); }, [q.data?.length]);
  if (q.isError) return null;
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try { await post(`/jobs/${jobId}/messages`, { text }); setText(""); qc.invalidateQueries({ queryKey: ["messages", jobId] }); }
    catch (er) { toast.error(errMsg(er)); }
    finally { setBusy(false); }
  };
  return (
    <Box title={`Chat con ${otherName ?? ""}`} testid="job-chat">
      <div ref={ref} className="max-h-72 space-y-2 overflow-y-auto rounded-2xl bg-slate-50 p-3">
        {!q.data?.length ? <p className="py-6 text-center text-sm text-slate-500">Aún no hay mensajes.</p> : q.data.map((m, i) => {
          const mine = m.sender_id === meId;
          return (
            <div key={m.id ?? i} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-orange-600 text-white" : "bg-white text-slate-800 shadow-sm"}`}>
                {!mine && <div className="text-[11px] font-semibold text-slate-500">{m.sender_name}</div>}
                {m.text}
                {m.created_at && <div className={`mt-0.5 text-[10px] ${mine ? "text-orange-100" : "text-slate-400"}`}>{fdatetime(m.created_at)}</div>}
              </div>
            </div>
          );
        })}
      </div>
      <form onSubmit={send} className="mt-3 flex gap-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Escribe un mensaje…" className="h-11" data-testid="chat-input" />
        <Button type="submit" size="icon" className="size-11 rounded-xl" disabled={busy} data-testid="chat-send-btn">{busy ? <Loader2 className="animate-spin" /> : <Send />}</Button>
      </form>
    </Box>
  );
};

export const payInvoice = async (invoiceId) => {
  try {
    const r = await post("/payments/checkout", { invoice_id: invoiceId, origin_url: window.location.origin });
    window.location.href = r.checkout_url;
  } catch (e) { toast.error(errMsg(e, "No se pudo iniciar el pago")); }
};

export const InvoiceBox = ({ invoiceId, canPay }) => {
  const { data: inv } = useQuery({ queryKey: ["invoices", invoiceId], queryFn: () => get(`/invoices/${invoiceId}`) });
  if (!inv) return null;
  return (
    <Box title={`Factura ${inv.number}`} testid="invoice-box">
      <div className="space-y-1 text-sm">
        {inv.lines.map((l, i) => <div key={i} className="flex justify-between gap-3"><span>{l.quantity} × {l.concept}</span><span>{eur(l.total)}</span></div>)}
        <div className="mt-2 flex justify-between border-t border-slate-200 pt-2"><span>Base</span><span>{eur(inv.base)}</span></div>
        <div className="flex justify-between"><span>IVA {inv.iva_rate}%</span><span>{eur(inv.iva)}</span></div>
        <div className="flex justify-between font-heading text-lg font-extrabold"><span>Total</span><span data-testid="invoice-total">{eur(inv.total)}</span></div>
        <div className="text-xs text-slate-500">Emitida {fdate(inv.issued_at)} · {inv.status === "pagada" ? `Pagada ${fdate(inv.paid_at)}` : "Pendiente de pago"}</div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {canPay && inv.status !== "pagada" && <Button className="rounded-xl" onClick={() => payInvoice(inv.id)} data-testid="pay-invoice-btn"><CreditCard />Pagar {eur(inv.total)} con tarjeta</Button>}
        <Button asChild variant="outline" className="rounded-xl"><a href={hzFile(`/invoices/${inv.id}/pdf`)} target="_blank" rel="noreferrer" data-testid="invoice-pdf-btn"><Download />PDF</a></Button>
      </div>
    </Box>
  );
};
